import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/components/ui/use-toast';
import {
  InventoryTransaction,
  Warehouse,
  IssueStockRequest,
  ReceiveStockRequest,
  TransferStockRequest,
  StockCheckResponse,
  InventoryFilters
} from '@/types/inventory';

export const useInventoryOperations = () => {
  const [loading, setLoading] = useState(false);

  // Fetch warehouses
  const fetchWarehouses = useCallback(async (): Promise<Warehouse[]> => {
    try {
      const { data, error } = await supabase
        .from('warehouse')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch warehouses',
        variant: 'destructive'
      });
      return [];
    }
  }, []);

  // Fetch inventory transactions with filters
  const fetchTransactions = useCallback(async (filters?: InventoryFilters): Promise<InventoryTransaction[]> => {
    try {
      let query = supabase
        .from('inventory_tx')
        .select(`
          *,
          item:bom_components!item_id(component_name, drawing_number),
          inventory_item:inventory_items!inventory_item_id(code, name),
          wh_from_data:warehouse!wh_from(name),
          wh_to_data:warehouse!wh_to(name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        query = query.or(`item.component_name.ilike.%${filters.search}%,note.ilike.%${filters.search}%`);
      }

      if (filters?.txn_types && filters.txn_types.length > 0) {
        query = query.in('txn_type', filters.txn_types);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      if (filters?.warehouses && filters.warehouses.length > 0) {
        query = query.or(
          `wh_from.in.(${filters.warehouses.join(',')}),wh_to.in.(${filters.warehouses.join(',')})`
        );
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      // Map the data to include joined fields
      // Prefer inventory_item data over bom_components data
      const mappedData: InventoryTransaction[] = data?.map((tx: any) => ({
        ...tx,
        item_name: tx.inventory_item?.name || tx.item?.component_name || 'Unknown Item',
        item_code: tx.inventory_item?.code || tx.item?.drawing_number || '',
        wh_from_name: tx.wh_from_data?.name || '',
        wh_to_name: tx.wh_to_data?.name || '',
        user_name: 'System'
      })) || [];

      return mappedData;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch inventory transactions',
        variant: 'destructive'
      });
      return [];
    }
  }, []);

  // Issue stock using RPC function
  const issueStock = useCallback(async (request: IssueStockRequest): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('fn_issue_stock', {
        item_id_param: request.item_id,
        wh_from_param: request.wh_from,
        qty_param: request.qty,
        ref_param: request.ref_table,
        ref_id_param: request.ref_id,
        note_param: request.note || null
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Stock issued successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Error issuing stock:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to issue stock',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Receive stock using RPC function
  const receiveStock = useCallback(async (request: ReceiveStockRequest): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('fn_receive_stock', {
        item_id_param: request.item_id,
        wh_to_param: request.wh_to,
        qty_param: request.qty,
        cost_param: request.unit_cost,
        ref_param: request.ref_table,
        ref_id_param: request.ref_id,
        note_param: request.note || null
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Stock received successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Error receiving stock:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to receive stock',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Transfer stock using RPC function
  const transferStock = useCallback(async (request: TransferStockRequest): Promise<boolean> => {
    setLoading(true);
    try {
      const { error } = await supabase.rpc('fn_transfer_stock', {
        item_id_param: request.item_id,
        wh_from_param: request.wh_from,
        wh_to_param: request.wh_to,
        qty_param: request.qty,
        ref_param: request.ref_table,
        ref_id_param: request.ref_id,
        note_param: request.note || null
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Stock transferred successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Error transferring stock:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to transfer stock',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Check stock availability
  const checkStock = useCallback(async (
    itemId: string,
    warehouseId: string,
    requiredQty: number
  ): Promise<StockCheckResponse | null> => {
    try {
      const { data, error } = await supabase.rpc('fn_check_stock', {
        item_id_param: itemId,
        wh_id_param: warehouseId,
        required_qty_param: requiredQty
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking stock:', error);
      return null;
    }
  }, []);

  // Create or update warehouse
  const saveWarehouse = useCallback(async (warehouse: Partial<Warehouse>): Promise<boolean> => {
    setLoading(true);
    try {
      if (warehouse.id) {
        // Update existing warehouse
        const { error } = await supabase
          .from('warehouse')
          .update({
            code: warehouse.code,
            name: warehouse.name,
            type: warehouse.type,
            is_active: warehouse.is_active
          })
          .eq('id', warehouse.id);

        if (error) throw error;
      } else {
        // Create new warehouse
        const { error } = await supabase
          .from('warehouse')
          .insert({
            code: warehouse.code,
            name: warehouse.name,
            type: warehouse.type,
            is_active: warehouse.is_active ?? true
          });

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: `Warehouse ${warehouse.id ? 'updated' : 'created'} successfully`,
      });

      return true;
    } catch (error: any) {
      console.error('Error saving warehouse:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save warehouse',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    fetchWarehouses,
    fetchTransactions,
    issueStock,
    receiveStock,
    transferStock,
    checkStock,
    saveWarehouse
  };
};
